/**
 * Actors — anything that occupies a tile and can hold intents.
 *
 * Players and creatures are the same shape on purpose. The wolves in M2-05 and
 * the boar in M5-04 want every movement rule a player has, and a body model
 * that only players own is a body model that has to be retrofitted the first
 * time something bites back.
 *
 * Note what an actor is *not*: it has no glyph, no colour, and no sprite. The
 * sim decides that a thing is a `player` at (12, 40) facing north; the client
 * decides that this looks like a white `@`. Presentation in world state would
 * put the renderer's vocabulary inside the determinism hash.
 */
/**
 * The eight compass directions, ordered clockwise from north. Movement is
 * 8-directional (M0-04); facing matters to combat (M2-03's reach and flanking)
 * long before it matters to the renderer.
 */
export const DIRECTIONS = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'];
/** Screen-space unit vectors: +x is east, **+y is south**. */
export const DIRECTION_VECTORS = {
    n: [0, -1],
    ne: [1, -1],
    e: [1, 0],
    se: [1, 1],
    s: [0, 1],
    sw: [-1, 1],
    w: [-1, 0],
    nw: [-1, -1],
};
/** Diagonals cost more to walk — see `MOVE_COST` in `movement.ts`. */
export function isDiagonal(direction) {
    const [dx, dy] = DIRECTION_VECTORS[direction];
    return dx !== 0 && dy !== 0;
}
/**
 * The direction of a single step, or `null` if the offset is not one — zero,
 * or longer than one tile. Used to validate a client's move before trusting
 * the direction it claims.
 */
export function directionOf(dx, dy) {
    if (!Number.isInteger(dx) || !Number.isInteger(dy))
        return null;
    if (dx === 0 && dy === 0)
        return null;
    if (Math.abs(dx) > 1 || Math.abs(dy) > 1)
        return null;
    for (const direction of DIRECTIONS) {
        const [vx, vy] = DIRECTION_VECTORS[direction];
        if (vx === dx && vy === dy)
            return direction;
    }
    return null;
}
export function isDirection(value) {
    return typeof value === 'string' && DIRECTIONS.includes(value);
}
/**
 * Chebyshev distance — the number of 8-directional steps between two tiles.
 * The right metric for a grid where a diagonal is one step, and the one
 * area-of-interest and "can they hear you" both use.
 */
export function chebyshev(ax, ay, bx, by) {
    return Math.max(Math.abs(ax - bx), Math.abs(ay - by));
}
export const ACTOR_KINDS = [
    'player',
    'creature',
    // M4-08. Somebody who lives here: a schedule, a voice and a body like
    // anybody else's. A kind rather than a flag, because the client draws them
    // differently and the wildlife rules must never mistake one for prey.
    'villager',
];
export function createActor(options) {
    return {
        id: options.id,
        kind: options.kind ?? 'player',
        name: options.name,
        x: options.x,
        y: options.y,
        facing: options.facing ?? 's',
        moveReadyAt: 0,
        sayReadyAt: 0,
    };
}
//# sourceMappingURL=actor.js.map